import React from 'react'
import { Container, Header, Divider } from 'semantic-ui-react'

import SEO from 'lib/components/SEO'
import ExternalLink from 'lib/components/ExternalLink'

import SpeedGraph from './SpeedGraph'
import CTA from './CTA'
import Section from './Section'

const Allergies = ({}) => {
  return (
    <>
      <SEO
        title="Undeclared allergens in recalled products in Canada | Recall Monitor"
        description="Learn about the most common undeclared allergens and their effects for recalled products in Canada. In 2019 over 75 products were recalled for undeclared allergens."
        canonical="https://recallmonitor.ca/why/allergies"
        type="article"
        image="https://recallmonitor.ca/static/why/otherallergens.jpg"
      />
      <Container text css={{ margin: '1rem 0' }}>
        <Header as="h1">
          Allergies
          <Header.Subheader>Keep up with undeclared allergens</Header.Subheader>
        </Header>
        <p>
          Every year hundreds of products are recalled for undeclared allergens.
          Since 2019 there have been <strong>227</strong> products that can put
          your health at risk. That is a product recall every{' '}
          <strong>2 days</strong>.
        </p>

        <p>
          How can consumers keep themselves protected? How can restaurants,
          cafes and food businesses ensure they are safe from contaminants in
          foods they serve?{' '}
          <strong>
            Just a few hours of delay might mean unknown exposure to these
            allergens.
          </strong>
        </p>

        <SpeedGraph />

        <p>
          Recall Monitor can help you keep track of active recalls for
          allergens, keeping you and your customers safe.
        </p>

        <p>
          Businesses can serve their customers better and avoid costly liability
          issues by protecting themselves against recalls on foods with
          undeclared allergens. Oversights can cause huge issues, like the case
          of a{' '}
          <ExternalLink href="https://www.cbc.ca/news/canada/montreal/sherbrooke-waiter-arrested-salmon-allergic-customer-1.3707052">
            Quebec waiter arrested after seafood puts allergic customer in coma
          </ExternalLink>{' '}
          or the{' '}
          <ExternalLink href="https://www.cbc.ca/news/canada/saskatchewan/man-with-nut-allergy-wins-cheesecake-lawsuit-1.1062258">
            Man with nut allergy wins cheesecake lawsuit
          </ExternalLink>
          .
        </p>

        <p>
          Here are some examples of{' '}
          <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/food-safety/food-allergies-intolerances/food-allergies.html">
            common food allergens
          </ExternalLink>{' '}
          and their effects on those with allergies from Health Canada since
          2019.
        </p>

        <Divider />

        <Section img="/static/why/eggs.jpg">
          <Header>Eggs</Header>
          <p>
            <strong>46</strong> products were recalled because they contained
            eggs without any indication on the packaging.
          </p>
          <p>
            Eggs are one of the most common allergy-causing foods for children.
            Learn why eggs are a{' '}
            <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/reports-publications/food-safety/eggs-priority-food-allergen.html">
              priority allergen
            </ExternalLink>
            .
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/peanuts.jpg" reversed>
          <Header>Peanuts</Header>
          <p>
            <strong>21</strong> products containing undeclared peanuts. Peanut
            allergy is one of the most common causes of severe allergy.
          </p>
          <p>
            Eating just 1 product laced with it can cause itchiness, hives,
            swelling, eczema, sneezing, asthma, abdominal pain, drop in blood
            pressure, diarrhea, cardiac arrest and anaphylaxis. Learn why
            peanuts are a{' '}
            <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/reports-publications/food-safety/peanuts-priority-food-allergen.html">
              priority food allergen
            </ExternalLink>
            .
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/wheat.jpg">
          <Header>Wheat & Gluten</Header>
          <p>
            <strong>43</strong> products that containing undeclared wheat. Those
            with gluten sensitivities are especially at risk of cosuming foods
            laced with gluten.
          </p>
          <p>
            Exposure to it can lead to breathing difficulties, nausea, hives,
            bloated stomach, and an inability to focus. In some people,
            anaphylaxis, a life-threatening allergic response, can occur.
          </p>
          <p>
            Learn why wheat and triticale are{' '}
            <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/reports-publications/food-safety/wheat-priority-food-allergen.html">
              priority food allergens
            </ExternalLink>
            .
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/milk.jpg" reversed>
          <Header>Milk & Dairy</Header>
          <p>
            <strong>43</strong> undeclared milk and dairy products recalled.
            Young children and even adults are at risk of getting exposed to
            these products. Signs and symptoms of milk allergy range from mild
            to severe and can include wheezing, vomiting, hives and digestive
            problems.
          </p>
          <p>
            Milk allergy can also cause anaphylaxis — a severe, life-threatening
            reaction. Learn why milk is a{' '}
            <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/reports-publications/food-safety/milk-priority-food-allergen.html">
              priority allergen
            </ExternalLink>
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/soy.jpg">
          <Header>Soy</Header>
          <p>
            <strong>13</strong> products with undeclared soy. Often, soy allergy
            starts in infancy with reaction to soy-based infant formula.
            Although most children outgrow soy allergy, some carry the allergy
            into adulthood.
          </p>
          <p>
            Mild signs and symptoms of soy allergy include hives or itching in
            and around the mouth. Learn why soy is a{' '}
            <ExternalLink href="https://www.canada.ca/en/health-canada/services/food-nutrition/reports-publications/food-safety/priority-food-allergen.html">
              priority food allergen
            </ExternalLink>
            .
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/otherallergens.jpg" reversed>
          <Header>Other Allergens</Header>
          <p>
            <strong>61</strong> products containing undeclared tree nuts,
            shellfish, sesame seeds, mustard, sulphites, fish and many more.
          </p>
        </Section>

        <Divider />

        <Header>The list goes on...</Header>
        <p>
          Were you aware of any products recalled because of undeclared
          allergens?
        </p>
        <p>
          Recall Monitor will help you get knowledgeable about recalled
          products. Don't be at the mercy of a news segment you may never see.
          Sign up now, and start receiving the alerts you care about -
          instantly.
        </p>
        <CTA />
      </Container>
    </>
  )
}

export default Allergies
